import { LayaGL } from "../../../../layagl/LayaGL";
import { DrawType } from "../../../../RenderEngine/RenderEnum/DrawType";
import { IndexFormat } from "../../../../RenderEngine/RenderEnum/IndexFormat";
import { MeshTopology } from "../../../../RenderEngine/RenderEnum/RenderPologyMode";
import { Pool } from "../../../../utils/Pool";
import { BufferUsage } from "../../../../RenderEngine/RenderEnum/BufferTargetType";
;
import { Web2DGraphicsIndexBatchBuffer } from "./Web2DGraphic2DBuffer";
import { WebRender2DPass } from "./WebRender2DPass";
import { ShaderDefines2D } from "../../../../webgl/shader/d2/ShaderDefines2D";
class BatchBuffer {
    constructor() {
        this.indexCount = 0;
        this.maxIndexCount = 0;
        this.bufferStates = new Map();
        this.indexBuffer = LayaGL.renderDeviceFactory.createIndexBuffer(BufferUsage.Dynamic);
        this.indexBuffer.indexType = IndexFormat.UInt16;
        this.wholeBuffer = new Web2DGraphicsIndexBatchBuffer();
        this.wholeBuffer.buffer = this.indexBuffer;
        if (!!LayaGL.renderEngine.gl) {
            this.add = this._addWebgl;
        }
        else {
            this.add = this._addWebgpu;
        }
    }
    _addWebgl(element) {
        let handle = element.owner.renderDataHandler;
        let blocks = handle._getBlocks();
        if (!blocks)
            return null;
        let cview = handle.getCloneViews()[element._index];
        let block = blocks[element._index];
        let vertexBuffer = block.vertexBuffer;
        let bufferState = this.bindBuffer(vertexBuffer);
        this.indexCount += cview.length;
        this.wholeBuffer._modifyOneView(cview);
        if (cview._geometry.bufferState !== bufferState) {
            cview._geometry.bufferState = bufferState;
        }
        WebRender2DPass.setBuffer(this.wholeBuffer);
        this.updateBufLength();
        return cview._geometry;
    }
    _addWebgpu(element) {
        let handle = element.owner.renderDataHandler;
        let blocks = handle._getBlocks();
        if (!blocks)
            return null;
        let cview = handle.getCloneViews()[element._index];
        let block = blocks[element._index];
        let vertexBuffer = block.vertexBuffer;
        let bufferState = this.bindBuffer(vertexBuffer);
        this.indexCount += cview.length;
        this.wholeBuffer._modifyOneView(cview);
        if (cview._geometry._bufferState !== bufferState) {
            cview._geometry.bufferState = bufferState;
        }
        WebRender2DPass.setBuffer(this.wholeBuffer);
        this.updateBufLength();
        return cview._geometry;
    }
    add(element) {
        return null;
    }
    updateBufLength() {
        if (this.maxIndexCount <= this.indexCount) {
            let nLength = Math.ceil(this.indexCount / _STEP_) * _STEP_;
            let byteLength = nLength * 2;
            this.indexBuffer._setIndexDataLength(byteLength);
            this.wholeBuffer._resetData(byteLength);
            this.maxIndexCount = nLength;
        }
    }
    bindBuffer(buffer) {
        let bufferState = this.bufferStates.get(buffer);
        if (!bufferState) {
            bufferState = LayaGL.renderDeviceFactory.createBufferState();
            bufferState.applyState([buffer], this.indexBuffer);
            this.bufferStates.set(buffer, bufferState);
        }
        return bufferState;
    }
    clear() {
        this.indexCount = 0;
        this.wholeBuffer.clearBufferViews();
    }
    destroy() {
        this.clear();
        this.bufferStates.forEach((bufferState) => {
            bufferState.destroy();
        });
        this.bufferStates.clear();
        this.indexBuffer.destroy();
        this.indexBuffer = null;
        this.wholeBuffer.destroy();
        this.wholeBuffer = null;
    }
}
class BatchContext {
    constructor() {
        this.textureId = 0;
        this.globalAlpha = 1;
        this.clipInfo = null;
        this.subShader = null;
        this.bufferState = null;
        this.primitiveShaderData = null;
        this.materialShaderData = null;
        this.typeKey = 0;
        this.textureKey = 0;
        this.globalRenderData = null;
        this.fillTexture = false;
        let isWebgl = !!LayaGL.renderEngine.gl;
        if (isWebgl) {
            this.setHead = this._setHeadWebgl;
            this.isCompatible = this._isCompatibleWebgl;
        }
        else {
            this.setHead = this._setHeadWebgpu;
            this.isCompatible = this._isCompatibleWebgpu;
        }
    }
    _setHeadWebgl(element) {
        this.primitiveShaderData = element.primitiveShaderData;
        this.materialShaderData = element.materialShaderData;
        this.subShader = element.subShader;
        this.bufferState = element.geometry.bufferState;
        this.typeKey = element.typeKey;
        this.textureKey = element.textureKey;
        this.textureId = element.textureKey & (~((1 << ShaderDefines2D.SHADER_DEFINE_BITS) - 1));
        this.globalAlpha = element.owner.globalAlpha;
        this.clipInfo = element.owner.getClipInfo();
        this.globalRenderData = element.owner.globalRenderData;
        this.fillTexture = !!(element.typeKey & 64);
        this.texRange = this.primitiveShaderData.getVector(ShaderDefines2D.UNIFORM_TEXRANGE);
    }
    _setHeadWebgpu(element) {
        this.primitiveShaderData = element._primitiveShaderData;
        this.materialShaderData = element._materialShaderData;
        this.subShader = element._subShader;
        this.bufferState = element.geometry._bufferState;
        this.typeKey = element.typeKey;
        this.textureKey = element.textureKey;
        this.textureId = element.textureKey & (~((1 << ShaderDefines2D.SHADER_DEFINE_BITS) - 1));
        this.globalAlpha = element.owner.globalAlpha;
        this.clipInfo = element.owner.getClipInfo();
        this.globalRenderData = element.owner.globalRenderData;
        this.fillTexture = !!(element.typeKey & 64);
        this.texRange = this.primitiveShaderData.getVector(ShaderDefines2D.UNIFORM_TEXRANGE);
    }
    setHead(element) { }
    _isCompatibleWebgl(element) {
        if (this.typeKey & 32)
            return false;
        if (element.typeKey & 32) {
            return false;
        }
        if (this.typeKey !== element.typeKey || this.globalAlpha !== element.owner.globalAlpha) {
            return false;
        }
        let defineMask = (1 << ShaderDefines2D.SHADER_DEFINE_BITS) - 1;
        if ((this.textureKey & defineMask) !== (element.textureKey & defineMask)) {
            return false;
        }
        let elementTexId = element.textureKey & (~defineMask);
        if (elementTexId !== 0 && elementTexId !== this.textureId && this.textureId !== 0)
            return false;
        if (this.subShader !== element.subShader ||
            this.bufferState !== element.geometry.bufferState ||
            this.clipInfo !== element.owner.getClipInfo() ||
            element.owner.globalRenderData !== this.globalRenderData) {
            return false;
        }
        if ((this.typeKey & 16) !== 0 && element.materialShaderData !== this.materialShaderData) {
            return false;
        }
        if (this.fillTexture) {
            if (!element.primitiveShaderData.getVector(ShaderDefines2D.UNIFORM_TEXRANGE).equal(this.texRange))
                return false;
        }
        if (this.textureId === 0 && elementTexId !== 0) {
            this.textureId = elementTexId;
            this.primitiveShaderData = element.primitiveShaderData;
            this.textureKey = element.textureKey;
        }
        return true;
    }
    _isCompatibleWebgpu(element) {
        if (this.typeKey & 32)
            return false;
        if (element.typeKey & 32) {
            return false;
        }
        if (this.typeKey !== element.typeKey || this.globalAlpha !== element.owner.globalAlpha) {
            return false;
        }
        let defineMask = (1 << ShaderDefines2D.SHADER_DEFINE_BITS) - 1;
        if ((this.textureKey & defineMask) !== (element.textureKey & defineMask)) {
            return false;
        }
        let elementTexId = element.textureKey & (~defineMask);
        if (elementTexId !== 0 && elementTexId !== this.textureId && this.textureId !== 0)
            return false;
        if (this.subShader !== element.subShader ||
            this.bufferState !== element.geometry.bufferState ||
            this.clipInfo !== element.owner.getClipInfo() ||
            element.owner.globalRenderData !== this.globalRenderData) {
            return false;
        }
        if ((this.typeKey & 16) !== 0 && element._materialShaderData !== this.materialShaderData) {
            return false;
        }
        if (this.fillTexture) {
            let primitiveShaderData = element._primitiveShaderData;
            if (!primitiveShaderData.getVector(ShaderDefines2D.UNIFORM_TEXRANGE).equal(this.texRange))
                return false;
        }
        if (this.textureId === 0 && elementTexId !== 0) {
            this.textureId = elementTexId;
            this.primitiveShaderData = element._primitiveShaderData;
            this.textureKey = element.textureKey;
        }
        return true;
    }
    isCompatible(element) {
        return true;
    }
}
export class WebGraphicsBatch {
    constructor() {
        this._buffer = new BatchBuffer();
        this._merged = [];
        this._context = new BatchContext();
    }
    reset() {
        this._buffer.clear();
        WebGraphicsBatch._pool.recover(this._merged);
    }
    destroy() {
        this._buffer.destroy();
        WebGraphicsBatch._pool.recover(this._merged);
    }
    batch(list, start, end, allowReorder) {
        let elementArray = list.elements;
        let ctx = this._context;
        ctx.setHead(elementArray[start]);
        let cnt = end - start + 1;
        if (cnt > 1000)
            allowReorder = false;
        if (allowReorder) {
            if (elementFlags == null)
                initCache(1000);
            let headGroup = 0;
            let maxGroup = 1;
            let indiceLen = 1;
            elementIndice[0] = start;
            elementFlags[0] = 0;
            for (let i = 1; i < cnt; i++) {
                let element = elementArray[start + i];
                elementFlags[i] = -1;
                let rect = element.owner.rect;
                rectLeftCache[i] = rect.x;
                rectTopCache[i] = rect.y;
                rectRightCache[i] = rect.x + rect.width;
                rectBottomCache[i] = rect.y + rect.height;
            }
            for (let i = 1; i < cnt; i++) {
                let element = elementArray[start + i];
                let group = elementFlags[i];
                if (group === -2) {
                    continue;
                }
                if (group !== -1) {
                    if (group === headGroup) {
                        elementIndice[indiceLen++] = start + i;
                        continue;
                    }
                }
                else {
                    if (ctx.isCompatible(element)) {
                        elementIndice[indiceLen++] = start + i;
                        continue;
                    }
                    elementFlags[i] = group = maxGroup++;
                }
                for (let j = i + 1; j < cnt; j++) {
                    let element2 = elementArray[start + j];
                    if (elementFlags[j] !== -1) {
                        if (elementFlags[j] !== headGroup)
                            continue;
                    }
                    else {
                        if (!ctx.isCompatible(element2))
                            continue;
                    }
                    for (let k = j - 1; k >= i; k--) {
                        if (elementFlags[k] !== -2
                            && rectLeftCache[j] < rectRightCache[k] && rectRightCache[j] > rectLeftCache[k]
                            && rectTopCache[j] < rectBottomCache[k] && rectBottomCache[j] > rectTopCache[k]) {
                            element2 = null;
                            break;
                        }
                    }
                    if (element2 != null) {
                        elementIndice[indiceLen++] = start + j;
                        elementFlags[j] = -2;
                    }
                    else if (ctx.textureId !== 0)
                        elementFlags[j] = headGroup;
                }
                list.add(this.merge(elementArray, 0, indiceLen - 1, ctx, elementIndice));
                indiceLen = 1;
                elementIndice[0] = start + i;
                headGroup = group;
                ctx.setHead(element);
            }
            list.add(this.merge(elementArray, 0, indiceLen - 1, ctx, elementIndice));
        }
        else {
            let batchStart = start;
            for (let i = start + 1; i <= end; i++) {
                let element = elementArray[i];
                if (!ctx.isCompatible(element)) {
                    list.add(this.merge(elementArray, batchStart, i - 1, ctx));
                    batchStart = i;
                    ctx.setHead(element);
                }
            }
            list.add(this.merge(elementArray, batchStart, end, ctx));
        }
    }
    merge(elementArray, start, end, batchContext, indice) {
        if (start === end) {
            let element = elementArray[indice !== undefined ? indice[start] : start];
            this._buffer.add(element);
            return element;
        }
        let staticBatchRenderElement = WebGraphicsBatch._pool.take();
        this._merged.push(staticBatchRenderElement);
        let batchedGeometry = staticBatchRenderElement.geometry;
        let currentOffset = 0;
        let currentCount = 0;
        let isFirst = true;
        for (let i = start; i <= end; i++) {
            let element = elementArray[indice !== undefined ? indice[i] : i];
            let geometry = this._buffer.add(element) || element.geometry;
            if (i === start) {
                batchedGeometry.bufferState = geometry.bufferState;
                staticBatchRenderElement.materialShaderData = element.materialShaderData;
                staticBatchRenderElement.value2DShaderData = element.value2DShaderData;
                staticBatchRenderElement.subShader = element.subShader;
                staticBatchRenderElement.renderStateIsBySprite = element.renderStateIsBySprite;
                staticBatchRenderElement.primitiveShaderData = batchContext.primitiveShaderData;
                staticBatchRenderElement.owner = element.owner;
                staticBatchRenderElement.typeKey = batchContext.typeKey;
                staticBatchRenderElement.textureKey = batchContext.textureKey;
            }
            let drawParam = geometry.drawParams.elements;
            let drawLength = geometry.drawParams.length;
            for (let j = 0; j < drawLength; j += 2) {
                let offset = drawParam[j];
                let count = drawParam[j + 1];
                if (isFirst) {
                    currentOffset = offset;
                    currentCount = count;
                    isFirst = false;
                    continue;
                }
                if (currentOffset + currentCount * 2 === offset) {
                    currentCount += count;
                }
                else {
                    batchedGeometry.setDrawElemenParams(currentCount, currentOffset);
                    currentOffset = offset;
                    currentCount = count;
                }
            }
        }
        if (!isFirst) {
            batchedGeometry.setDrawElemenParams(currentCount, currentOffset);
        }
        return staticBatchRenderElement;
    }
}
WebGraphicsBatch._pool = Pool.createPool2(() => {
    let element = LayaGL.render2DRenderPassFactory.createPrimitiveRenderElement2D();
    element.geometry = LayaGL.renderDeviceFactory.createRenderGeometryElement(MeshTopology.Triangles, DrawType.DrawElement);
    element.geometry.indexFormat = IndexFormat.UInt16;
    element.nodeCommonMap = ["Sprite2D"];
    element.renderStateIsBySprite = false;
    return element;
}, null, element => {
    element.geometry.clearRenderParams();
    element.geometry.bufferState = null;
    element.materialShaderData = null;
    element.value2DShaderData = null;
    element.primitiveShaderData = null;
    element.subShader = null;
    element.owner = null;
    element.renderStateIsBySprite = false;
    element.globalShaderData = null;
    element.typeKey = 0;
    element.textureKey = 0;
});
const _STEP_ = 1024;
var elementFlags;
var elementIndice;
var rectLeftCache;
var rectTopCache;
var rectRightCache;
var rectBottomCache;
function initCache(maxElements) {
    elementFlags = new Int16Array(maxElements);
    elementIndice = new Int16Array(maxElements);
    rectLeftCache = new Float32Array(maxElements);
    rectTopCache = new Float32Array(maxElements);
    rectRightCache = new Float32Array(maxElements);
    rectBottomCache = new Float32Array(maxElements);
}
